import { makeAutoObservable } from 'mobx';
import { ReviewsModel, Review, ReviewStats } from '../../model/professional/reviews-model';
import toastStore from '../../store/toast-store';
import reviewsStore from '../../store/reviews-store';
import uiStore from '../../store/ui-store';

class ReviewsViewModel {
  isLoading: boolean = false;
  error: string | null = null;
  private reviewsModel: ReviewsModel;

  /**
   * Gère les états de chargement de manière centralisée
   * @param isLoading État de chargement à définir
   * @param resetError Si vrai, réinitialise également les erreurs
   */
  private setLoadingStates(isLoading: boolean, resetError: boolean = true): void {
    this.setLoading(isLoading);
    if (resetError && !isLoading) {
      this.setError(null);
    }
  }
  
  /**
   * Gère les erreurs de manière centralisée
   * @param message Message d'erreur à afficher
   * @param error Objet d'erreur optionnel pour le logging
   */
  private handleError(message: string, error?: any): void {
    console.error(message, error);
    this.setError(message);
    toastStore.error(message);
  }

  setLoading(value: boolean): void {
    this.isLoading = value;
  }

  setError(value: string | null): void {
    this.error = value;
  }
  
  constructor() {
    makeAutoObservable(this);
    this.reviewsModel = ReviewsModel.getInstance();
  }

  /**
   * Formate la date d'un avis
   * @param dateString Date au format ISO
   * @returns Date formatée
   */
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  }

  /**
   * Récupère tous les avis du professionnel connecté
   */
  async fetchMyReviews(): Promise<void> {
    this.setLoadingStates(true);
    try {
      const response = await this.reviewsModel.getMyReviews();
      if (response.success) {
        reviewsStore.setReviews(response.reviews);
        reviewsStore.setCount(response.count);
      } else {
        this.handleError(response.message);
      }
    } catch (error: any) {
      this.handleError(`Erreur lors de la récupération des avis: ${error.message}`, error);
    } finally {
      this.setLoadingStates(false);
    }
  }

  /**
   * Récupère les statistiques d'avis du professionnel connecté
   */
  async fetchMyStats(): Promise<void> {
    this.setLoadingStates(true);
    try {
      const response = await this.reviewsModel.getMyStats();
      if (response.success) {

        const stats: ReviewStats = {
          averageRating: response.review?.rating || 0,
          count: response.count || 0,
          ratingDistribution: response.ratingDistribution || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
        };
        reviewsStore.setStats(stats);
      } else {
        this.handleError(response.message);
      }
    } catch (error: any) {
      this.handleError(`Erreur lors de la récupération des statistiques: ${error.message}`, error);
    } finally {
      this.setLoadingStates(false);
    }
  }
  
  /**
   * Répond à un avis client
   * @param reviewId ID de l'avis
   * @param response Texte de la réponse
   */
  async respondToReview(reviewId: string, response: string): Promise<boolean> {
    this.setLoadingStates(true);
    try {
      const result = await this.reviewsModel.respondToReview(reviewId, { response });
      if (result.success) {
        toastStore.success('Votre réponse a été enregistrée avec succès');

        if (result.review) {
          reviewsStore.updateReview(result.review);
        } else {
          await this.fetchMyReviews();
        }
        return true;
      } else {
        this.handleError(result.message);
        return false;
      }
    } catch (error: any) {
      this.handleError(`Erreur lors de l'envoi de la réponse: ${error.message}`, error);
      return false;
    } finally {
      this.setLoadingStates(false);
    }
  }

  /**
   * Filtre les avis selon un terme de recherche
   * @param searchTerm Terme de recherche
   */
  filterReviews(searchTerm: string): void {
    uiStore.setSearchTerm(searchTerm);
  }
  
  /**
   * Obtient le terme de recherche actuel
   */
  get searchTerm(): string {
    return uiStore.searchTerm;
  }
  
  // Pagination
  private currentPage: number = 1;
  private itemsPerPage: number = 4;
  /**
   * Définit la page courante
   * @param page Numéro de page
   */
  setCurrentPage(page: number): void {
    this.currentPage = page;
  }

  /**
   * Définit le nombre d'avis par page
   * @param count Nombre d'avis par page
   */
  setItemsPerPage(count: number): void {
    this.itemsPerPage = count;
  }

  /**
   * Obtient la page courante
   */
  get currentPageNumber(): number {
    return this.currentPage;
  }

  /**
   * Obtient le nombre d'avis par page
   */
  get itemsPerPageCount(): number {
    return this.itemsPerPage;
  }

  /**
   * Obtient le nombre total de pages
   */
  get totalPages(): number {
    return Math.ceil(this.filteredReviewsBeforePagination.length / this.itemsPerPage);
  }

  /**
   * Obtient les avis filtrés selon le terme de recherche (avant pagination)
   */
  get filteredReviewsBeforePagination(): Review[] {
    const reviews = reviewsStore.allReviews || [];
    const searchTerm = uiStore.searchTerm.toLowerCase();

    if (searchTerm === '') {
      return reviews;
    }

    return reviews.filter(review => {
      return (
        (review.client?.firstName && review.client.firstName.toLowerCase().includes(searchTerm)) ||
        (review.client?.lastName && review.client.lastName.toLowerCase().includes(searchTerm)) ||
        (review.comment && review.comment.toLowerCase().includes(searchTerm)) ||
        (review.response && review.response.toLowerCase().includes(searchTerm))
      );
    });
  }

  /**
   * Obtient les avis filtrés selon le terme de recherche et paginés
   */
  get filteredReviews(): Review[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    return this.filteredReviewsBeforePagination.slice(startIndex, startIndex + this.itemsPerPage);
  }
  
  /**
   * Obtient les statistiques d'avis
   */
  get stats(): ReviewStats | null {
    return reviewsStore.stats;
  }
  
  /**
   * Obtient le nombre total d'avis
   */
  get reviewCount(): number {
    return reviewsStore.count;
  }
}

const reviewsViewModel = new ReviewsViewModel();

// Export pour les cas non-React (si nécessaire)
export default reviewsViewModel;
